import React, { useEffect, useState } from 'react';
import { PlusIcon, SearchIcon, EditIcon, UserIcon, ClockIcon, MessageSquareIcon, AlertTriangleIcon, StickyNoteIcon, DownloadIcon, ArchiveIcon, PowerOffIcon, CheckCircleIcon, RotateCcwIcon, XCircleIcon, LockIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Textarea } from '../ui/Textarea';
import { Modal } from '../ui/Modal';
import { Badge } from '../ui/Badge';
import { Pagination } from '../ui/Pagination';
import { useData } from '../../contexts/DataContext';
import { usePermissions } from '../../hooks/usePermissions';
import { usePagination } from '../../hooks/usePagination';
import { useSortableData } from '../../hooks/useSortableData';
import { SortableHeader } from '../ui/SortableHeader';
import { useAuth } from '../../contexts/AuthContext';
import { FollowUpNote } from '../../types';
import { exportFollowUpNotesToExcel } from '../../utils/excelExport';
import { exportFollowUpNotesToPdf } from '../../utils/pdfExport';
import { ExcelExportModal } from '../ui/ExcelExportModal';
import { PdfExportModal } from '../ui/PdfExportModal';
// ============ SAFE STRING HELPER ============
// Guarantees any value becomes a renderable string - NEVER an object
function ss(v: any): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (v instanceof Date) {
    try {
      return v.toISOString();
    } catch {
      return '';
    }
  }
  try {
    return JSON.stringify(v);
  } catch {
    return '';
  }
}
// Safe date formatter
function safeFmtDate(date: any): string {
  try {
    if (!date) return 'Sin fecha';
    const d = date instanceof Date ? date : new Date(typeof date === 'string' || typeof date === 'number' ? date : Date.now());
    if (isNaN(d.getTime())) return 'Fecha inválida';
    return d.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }) + ' • ' + d.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return 'Sin fecha';
  }
}
// Sanitize a single note - every field becomes a safe primitive
function sanitizeNote(raw: any): {
  id: string;
  userId: string;
  userName: string;
  contactName: string;
  subject: string;
  description: string;
  createdAt: Date;
} {
  if (!raw || typeof raw !== 'object') {
    return {
      id: '',
      userId: '',
      userName: '',
      contactName: '',
      subject: '',
      description: '',
      createdAt: new Date()
    };
  }
  return {
    id: ss(raw.id),
    userId: ss(raw.userId),
    userName: ss(raw.userName),
    contactName: ss(raw.contactName),
    subject: ss(raw.subject),
    description: ss(raw.description),
    createdAt: raw.createdAt instanceof Date ? raw.createdAt : new Date(typeof raw.createdAt === 'string' || typeof raw.createdAt === 'number' ? raw.createdAt : Date.now())
  };
}
export function FollowUpNotes() {
  // ============ DATA ============
  const dataCtx = useData();
  const permCtx = usePermissions();
  const {
    user
  } = useAuth();
  const isAdmin = user?.role === 'administrador';
  const userRoleNorm = user?.role?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') || '';
  const canExportExcel = isAdmin || user?.role === 'terapeuta' || user?.role === 'supervisor/a' || userRoleNorm === 'recepcion' || userRoleNorm === 'recepcionista';
  const canExportPdf = isAdmin || user?.role === 'terapeuta' || user?.role === 'supervisor' || userRoleNorm === 'recepcion' || userRoleNorm === 'recepcionista';
  const readOnly = permCtx.isReadOnly('followUpNotes');
  // Sanitize ALL notes immediately - no raw data ever reaches JSX
  const rawNotes = dataCtx.followUpNotes;
  const notes = (Array.isArray(rawNotes) ? rawNotes : []).map(sanitizeNote);
  // ============ STATE ============
  const [searchTerm, setSearchTerm] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    contactName: '',
    subject: '',
    description: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isExporting, setIsExporting] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isPdfExporting, setIsPdfExporting] = useState(false);
  const [isPdfExportModalOpen, setIsPdfExportModalOpen] = useState(false);
  const [deleteNoteId, setDeleteNoteId] = useState<string | null>(null);
  // Archive/Deactivate
  const STORAGE_KEY = 'savidna-followup-notes-status';
  const [itemStatuses, setItemStatuses] = useState<Record<string, string>>({});
  const [viewMode, setViewMode] = useState<string>('active');
  const [actionConfirm, setActionConfirm] = useState<{
    id: string;
    type: string;
  } | null>(null);
  const [patientStatuses, setPatientStatuses] = useState<Record<string, string>>({});
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          setItemStatuses(parsed);
        }
      }
      const ps = localStorage.getItem('savidna-patients-status');
      if (ps) {
        const parsed = JSON.parse(ps);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          setPatientStatuses(parsed);
        }
      }
    } catch (e) {
      console.warn('Error loading statuses:', e);
    }
  }, []);
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(itemStatuses));
    } catch {}
  }, [itemStatuses]);
  // ============ DERIVED DATA ============
  const currentUserDoctor = dataCtx.doctors.find((d: any) => ss(d.email).toLowerCase() === ss(user?.email).toLowerCase());
  const isDoctor = !!currentUserDoctor && !isAdmin;
  const availablePatients = isDoctor && currentUserDoctor ? dataCtx.patients.filter((p: any) => !patientStatuses[ss(p.id)] && dataCtx.appointments.some((a: any) => ss(a.patientId) === ss(p.id) && ss(a.doctorId) === ss(currentUserDoctor.id) && ss(a.status) === 'completada')) : dataCtx.patients.filter((p: any) => !patientStatuses[ss(p.id)]);
  const getStatus = (id: string): string => itemStatuses[id] || 'active';
  // Filter
  const filteredNotes = notes.filter(note => {
    const term = searchTerm.toLowerCase();
    const matchesSearch = !term || note.contactName.toLowerCase().includes(term) || note.subject.toLowerCase().includes(term) || note.description.toLowerCase().includes(term) || note.userName.toLowerCase().includes(term);
    return matchesSearch && getStatus(note.id) === viewMode;
  });
  const {
    sortedItems: sortedNotes,
    requestSort: noteRequestSort,
    isSorted: noteIsSorted
  } = useSortableData(filteredNotes, {
    key: 'createdAt',
    direction: 'desc'
  });
  // Pagination
  const pagination = usePagination(sortedNotes, {
    itemsPerPage: 10
  });
  // Counts
  const activeCount = notes.filter(n => getStatus(n.id) === 'active').length;
  const archivedCount = notes.filter(n => getStatus(n.id) === 'archived').length;
  const deactivatedCount = notes.filter(n => getStatus(n.id) === 'deactivated').length;
  // ============ HANDLERS ============
  const openNewForm = () => {
    setEditingNoteId(null);
    setFormData({
      contactName: '',
      subject: '',
      description: ''
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  const openEditForm = (note: ReturnType<typeof sanitizeNote>) => {
    setEditingNoteId(note.id);
    setFormData({
      contactName: note.contactName,
      subject: note.subject,
      description: note.description
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!formData.contactName.trim()) errors.contactName = 'Debe seleccionar un paciente';
    if (!formData.subject.trim()) errors.subject = 'El asunto es requerido';
    if (!formData.description.trim()) errors.description = 'La descripción es requerida';
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    try {
      if (editingNoteId) {
        await dataCtx.updateFollowUpNote(editingNoteId, formData);
      } else {
        await dataCtx.addFollowUpNote({
          userId: ss(user?.id),
          userName: ss(user?.name) || 'Usuario',
          ...formData
        });
      }
    } catch (err) {
      console.error('Error saving note:', err);
    }
    setIsFormOpen(false);
  };
  const handleDelete = async () => {
    if (deleteNoteId) {
      try {
        await dataCtx.deleteFollowUpNote(deleteNoteId);
      } catch (err) {
        console.error('Error deleting:', err);
      }
      setIsDeleteConfirmOpen(false);
      setDeleteNoteId(null);
    }
  };
  const handleArchive = (id: string) => {
    setItemStatuses(prev => ({
      ...prev,
      [id]: 'archived'
    }));
    dataCtx.updateFollowUpNote(id, {
      status: 'archived'
    } as any).catch(() => {});
    setActionConfirm(null);
  };
  const handleDeactivate = (id: string) => {
    setItemStatuses(prev => ({
      ...prev,
      [id]: 'deactivated'
    }));
    dataCtx.updateFollowUpNote(id, {
      status: 'deactivated'
    } as any).catch(() => {});
    setActionConfirm(null);
  };
  const handleRestore = (id: string) => {
    setItemStatuses(prev => {
      const n = {
        ...prev
      };
      delete n[id];
      return n;
    });
    dataCtx.updateFollowUpNote(id, {
      status: 'active'
    } as any).catch(() => {});
  };
  const handleExportToExcel = async (dateFrom: string | null, dateTo: string | null) => {
    setIsExporting(true);
    await new Promise(r => setTimeout(r, 300));
    try {
      let dataToExport = rawNotes;
      if (dateFrom && dateTo) {
        dataToExport = rawNotes.filter((n: any) => {
          const dateStr = new Date(n.createdAt).toISOString().split('T')[0];
          return dateStr >= dateFrom && dateStr <= dateTo;
        });
      }
      exportFollowUpNotesToExcel(dataToExport);
    } catch (e) {
      console.error('Export error:', e);
    }
    setIsExporting(false);
    setIsExportModalOpen(false);
  };
  const handleExportToPdf = async (dateFrom: string | null, dateTo: string | null) => {
    setIsPdfExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    let dataToExport = rawNotes;
    if (dateFrom && dateTo) {
      dataToExport = rawNotes.filter((n: any) => {
        const dateStr = new Date(n.createdAt).toISOString().split('T')[0];
        return dateStr >= dateFrom && dateStr <= dateTo;
      });
    }
    exportFollowUpNotesToPdf(dataToExport);
    setIsPdfExporting(false);
    setIsPdfExportModalOpen(false);
  };
  // ============ PERMISSION CHECK ============
  if (!permCtx.canView('followUpNotes')) {
    return <div className="flex flex-col items-center justify-center h-[60vh] text-center p-6" data-id="element-2350">
        <MessageSquareIcon className="w-16 h-16 text-gray-300 mb-4" data-id="element-2351" />
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2" data-id="element-2352">
          Acceso Restringido
        </h2>
        <p className="text-gray-500 dark:text-gray-400 max-w-md" data-id="element-2353">
          No tienes permisos para ver las notas de seguimiento.
        </p>
      </div>;
  }
  // ============ RENDER ============
  const deleteNote = deleteNoteId ? notes.find(n => n.id === deleteNoteId) : null;
  return <div className="space-y-6" data-id="element-2354">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-amber-600 via-amber-500 to-orange-500 p-6 text-white shadow-xl" data-id="element-2355">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-2356" />
        <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-id="element-2357">
          <div data-id="element-2358">
            <div className="flex items-center gap-3 mb-2" data-id="element-2359">
              <div className="p-2.5 bg-white/15 backdrop-blur-sm rounded-xl" data-id="element-2360">
                <StickyNoteIcon className="w-6 h-6" data-id="element-2361" />
              </div>
              <h1 className="text-2xl font-bold" data-id="element-2362">{'Reportes diarios'}</h1>
            </div>
            <p className="text-amber-100 max-w-xl" data-id="element-2363">
              {'Registro de comunicaciones y seguimientos con contactos'}
              {readOnly && <span className="ml-2 bg-white/20 px-2 py-0.5 rounded text-xs font-medium" data-id="element-2364">
                  {'Solo lectura'}
                </span>}
            </p>
          </div>
          <div className="flex gap-3" data-id="element-2365">
            {canExportPdf && <Button variant="outline" leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-2367" />} onClick={() => setIsPdfExportModalOpen(true)} className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm" data-id="element-2366">
                Exportar PDF
              </Button>}
            {canExportExcel && <Button variant="outline" leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-2369" />} onClick={() => setIsExportModalOpen(true)} isLoading={isExporting} className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm" data-id="element-2368">
                Exportar Excel
              </Button>}
            {permCtx.canCreate('followUpNotes') && <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-2371" />} onClick={openNewForm} className="bg-white text-amber-700 hover:bg-amber-50 shadow-lg shadow-amber-900/20" data-id="element-2370">
                {'Nueva Nota'}
              </Button>}
          </div>
        </div>
      </div>

      {readOnly && <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-amber-700 dark:text-amber-300 text-sm flex items-center gap-2" data-id="element-2372">
          <LockIcon className="w-4 h-4" data-id="element-2373" />
          <span data-id="element-2374">
            {'Solo tienes permisos de lectura. No puedes crear, editar o eliminar notas de seguimiento.'}
          </span>
        </div>}

      {/* Search + Tabs */}
      <div className="flex flex-col md:flex-row gap-4 justify-between items-end" data-id="element-2375">
        <div className="w-full md:w-96 relative" data-id="element-2376">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-2377" />
          <Input placeholder="Buscar por paciente, asunto o descripción..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700" data-id="element-2378" />
        </div>
        <div className="flex items-center gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl w-full md:w-auto overflow-x-auto" data-id="element-2379">
          {[{
          key: 'active',
          label: 'Activos',
          count: activeCount,
          Icon: CheckCircleIcon
        }, {
          key: 'archived',
          label: 'Archivados',
          count: archivedCount,
          Icon: ArchiveIcon
        }, {
          key: 'deactivated',
          label: 'Desactivados',
          count: deactivatedCount,
          Icon: XCircleIcon
        }].map(tab => <button key={tab.key} onClick={() => setViewMode(tab.key)} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${viewMode === tab.key ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} data-id="element-2380">
              <tab.Icon className="w-4 h-4" data-id="element-2381" />
              {tab.label}
              <span className="text-xs bg-gray-200 dark:bg-gray-600 px-1.5 py-0.5 rounded-full" data-id="element-2382">
                {String(tab.count)}
              </span>
            </button>)}
        </div>
      </div>

      {/* Table */}
      <Card padding="none" className="overflow-hidden border-0 shadow-lg" data-id="element-2383">
        <div className="h-1 bg-gradient-to-r from-amber-500 to-orange-500" data-id="element-2384" />
        <div className="overflow-x-auto" data-id="element-2385">
          <table className="w-full" data-id="element-2386">
            <thead data-id="element-2387">
              <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50" data-id="element-2388">
                <SortableHeader label="Fecha y Hora" sortKey="createdAt" currentDirection={noteIsSorted('createdAt')} onSort={noteRequestSort} data-id="element-2389" />
                <SortableHeader label="Registrado por" sortKey="userName" currentDirection={noteIsSorted('userName')} onSort={noteRequestSort} data-id="element-2390" />
                <SortableHeader label="Paciente" sortKey="contactName" currentDirection={noteIsSorted('contactName')} onSort={noteRequestSort} data-id="element-2391" />
                <SortableHeader label="Asunto" sortKey="subject" currentDirection={noteIsSorted('subject')} onSort={noteRequestSort} data-id="element-2392" />
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-2393">
                  {'Descripción'}
                </th>
                <th className="text-right py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-2394">
                  {'Acciones'}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800" data-id="element-2395">
              {pagination.paginatedItems.length === 0 ? <tr data-id="element-2396">
                  <td colSpan={6} className="py-16 text-center text-gray-500 dark:text-gray-400" data-id="element-2397">
                    <div className="flex flex-col items-center justify-center" data-id="element-2398">
                      <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4" data-id="element-2399">
                        <StickyNoteIcon className="w-8 h-8 text-gray-400" data-id="element-2400" />
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1" data-id="element-2401">
                        {'No se encontraron notas'}
                      </h3>
                      <p className="text-sm" data-id="element-2402">
                        {searchTerm ? 'Intenta con otros términos de búsqueda' : viewMode === 'active' ? 'Comienza creando una nueva nota' : 'No hay notas en esta vista'}
                      </p>
                    </div>
                  </td>
                </tr> : pagination.paginatedItems.map(note => <tr key={note.id || Math.random()} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors group" data-id="element-2403">
                    <td className="py-4 px-6 whitespace-nowrap" data-id="element-2404">
                      <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300" data-id="element-2405">
                        <ClockIcon className="w-4 h-4 text-gray-400" data-id="element-2406" />
                        <span data-id="element-2407">{safeFmtDate(note.createdAt)}</span>
                      </div>
                    </td>
                    <td className="py-4 px-6 whitespace-nowrap" data-id="element-2408">
                      <div className="flex items-center gap-2" data-id="element-2409">
                        <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center text-amber-600 dark:text-amber-400" data-id="element-2410">
                          <UserIcon className="w-4 h-4" data-id="element-2411" />
                        </div>
                        <span className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-2412">
                          {note.userName}
                        </span>
                      </div>
                    </td>
                    <td className="py-4 px-6 whitespace-nowrap" data-id="element-2413">
                      <span className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-2414">
                        {note.contactName}
                      </span>
                    </td>
                    <td className="py-4 px-6" data-id="element-2415">
                      <Badge variant="info" data-id="element-2416">{note.subject}</Badge>
                    </td>
                    <td className="py-4 px-6" data-id="element-2417">
                      <p className="text-sm text-gray-700 dark:text-gray-300 max-w-xs truncate" title={note.description} data-id="element-2418">
                        {note.description}
                      </p>
                    </td>
                    <td className="py-4 px-6" data-id="element-2419">
                      <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-2420">
                        {viewMode === 'active' ? <>
                            {permCtx.canEdit('followUpNotes') && <button onClick={() => openEditForm(note)} className="p-2 text-gray-500 hover:text-sky-600 hover:bg-sky-50 dark:hover:bg-sky-900/20 rounded-lg transition-colors" title="Editar" data-id="element-2421">
                                <EditIcon className="w-4 h-4" data-id="element-2422" />
                              </button>}
                            <button onClick={() => setActionConfirm({
                      id: note.id,
                      type: 'archive'
                    })} className="p-2 text-gray-500 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg transition-colors" title="Archivar" data-id="element-2423">
                              <ArchiveIcon className="w-4 h-4" data-id="element-2424" />
                            </button>
                            <button onClick={() => setActionConfirm({
                      id: note.id,
                      type: 'deactivate'
                    })} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" title="Desactivar" data-id="element-2425">
                              <PowerOffIcon className="w-4 h-4" data-id="element-2426" />
                            </button>
                          </> : <button onClick={() => handleRestore(note.id)} className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors" title="Restaurar" data-id="element-2427">
                            <RotateCcwIcon className="w-4 h-4" data-id="element-2428" />
                          </button>}
                      </div>
                    </td>
                  </tr>)}
            </tbody>
          </table>
        </div>
        <div className="px-6 pb-4 border-t border-gray-100 dark:border-gray-700 pt-4" data-id="element-2429">
          <Pagination currentPage={pagination.currentPage} totalPages={pagination.totalPages} totalItems={pagination.totalItems} startIndex={pagination.startIndex} endIndex={pagination.endIndex} onPageChange={pagination.goToPage} onNext={pagination.nextPage} onPrev={pagination.prevPage} data-id="element-2430" />
        </div>
      </Card>

      {/* Form Modal */}
      {isFormOpen && <Modal isOpen={true} onClose={() => setIsFormOpen(false)} title={editingNoteId ? 'Editar Nota' : 'Nueva Nota de Seguimiento'} size="md" icon={<MessageSquareIcon className="w-5 h-5" data-id="element-2432" />} accentColor="amber" data-id="element-2431">
          <form onSubmit={handleSubmit} className="space-y-6" data-id="element-2433">
            <div data-id="element-2434">
              <div className="flex items-center gap-2 mb-4" data-id="element-2435">
                <span className="w-1.5 h-5 rounded-full bg-amber-500" data-id="element-2436"></span>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-2437">
                  {'Información de la Nota'}
                </h3>
              </div>
              <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 space-y-4 border border-gray-100 dark:border-gray-700/50" data-id="element-2438">
                <Select label="Paciente" options={[{
              value: '',
              label: 'Seleccionar paciente...'
            }, ...availablePatients.map((p: any) => ({
              value: ss(p.name),
              label: ss(p.name)
            }))]} value={formData.contactName} onChange={v => setFormData({
              ...formData,
              contactName: v
            })} error={formErrors.contactName} data-id="element-2439" />
                <Input label="Asunto" value={formData.subject} onChange={e => setFormData({
              ...formData,
              subject: e.target.value
            })} error={formErrors.subject} placeholder="Ej: Llamada de seguimiento, Correo enviado..." data-id="element-2440" />
              </div>
            </div>
            <div data-id="element-2441">
              <div className="flex items-center gap-2 mb-4" data-id="element-2442">
                <span className="w-1.5 h-5 rounded-full bg-orange-500" data-id="element-2443"></span>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-2444">
                  {'Descripción'}
                </h3>
              </div>
              <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 border border-gray-100 dark:border-gray-700/50" data-id="element-2445">
                <Textarea label="Detalle del seguimiento" value={formData.description} onChange={e => setFormData({
              ...formData,
              description: e.target.value
            })} error={formErrors.description} placeholder="Ej: Le escribí al paciente pero no respondió..." rows={4} data-id="element-2446" />
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-700" data-id="element-2447">
              <Button variant="outline" type="button" onClick={() => setIsFormOpen(false)} data-id="element-2448">
                {'Cancelar'}
              </Button>
              <Button type="submit" data-id="element-2449">
                {editingNoteId ? 'Guardar Cambios' : 'Crear Nota'}
              </Button>
            </div>
          </form>
        </Modal>}

      {/* Action Confirm Modal */}
      {actionConfirm && <Modal isOpen={true} onClose={() => setActionConfirm(null)} title={actionConfirm.type === 'archive' ? 'Archivar Nota' : 'Desactivar Nota'} size="sm" data-id="element-2450">
          <div className="space-y-4" data-id="element-2451">
            <p className="text-gray-600 dark:text-gray-400" data-id="element-2452">
              {actionConfirm.type === 'archive' ? '¿Estás seguro de que deseas archivar esta nota? Podrás restaurarla más tarde.' : '¿Estás seguro de que deseas desactivar esta nota? No aparecerá en las listas activas.'}
            </p>
            <div className="flex justify-end gap-3" data-id="element-2453">
              <Button variant="outline" onClick={() => setActionConfirm(null)} data-id="element-2454">
                {'Cancelar'}
              </Button>
              <Button variant="primary" onClick={() => {
            if (actionConfirm.type === 'archive') handleArchive(actionConfirm.id);else handleDeactivate(actionConfirm.id);
          }} data-id="element-2455">
                {actionConfirm.type === 'archive' ? 'Archivar' : 'Desactivar'}
              </Button>
            </div>
          </div>
        </Modal>}

      {/* Delete Confirm Modal */}
      {isDeleteConfirmOpen && deleteNote && <Modal isOpen={true} onClose={() => {
      setIsDeleteConfirmOpen(false);
      setDeleteNoteId(null);
    }} title="Eliminar Nota" size="sm" data-id="element-2456">
          <div className="space-y-4" data-id="element-2457">
            <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg" data-id="element-2458">
              <AlertTriangleIcon className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" data-id="element-2459" />
              <div data-id="element-2460">
                <p className="text-sm font-medium text-red-800 dark:text-red-200" data-id="element-2461">
                  {'¿Eliminar esta nota de seguimiento?'}
                </p>
                <p className="text-sm text-red-600 dark:text-red-400 mt-1" data-id="element-2462">
                  {'Esta acción no se puede deshacer.'}
                </p>
              </div>
            </div>
            <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg" data-id="element-2463">
              <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-2464">
                {deleteNote.contactName}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-2465">
                {deleteNote.subject}
              </p>
            </div>
            <div className="flex justify-end gap-3 pt-2" data-id="element-2466">
              <Button variant="outline" onClick={() => {
            setIsDeleteConfirmOpen(false);
            setDeleteNoteId(null);
          }} data-id="element-2467">
                {'Cancelar'}
              </Button>
              <Button variant="danger" onClick={handleDelete} data-id="element-2468">
                {'Eliminar'}
              </Button>
            </div>
          </div>
        </Modal>}

      {/* Excel Export Modal */}
      <ExcelExportModal isOpen={isExportModalOpen} onClose={() => setIsExportModalOpen(false)} onExport={handleExportToExcel} title="Exportar Notas de Seguimiento" isExporting={isExporting} data-id="element-2469" />

      {/* Pdf Export Modal */}
      <PdfExportModal isOpen={isPdfExportModalOpen} onClose={() => setIsPdfExportModalOpen(false)} onExport={handleExportToPdf} title="Exportar Notas a PDF" isExporting={isPdfExporting} data-id="element-2470" />
    </div>;
}